import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import Tab from 'react-bootstrap/esm/Tab';
import Nav from 'react-bootstrap/esm/Nav';
import Card from 'react-bootstrap/esm/Card';
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";

import { usePlan } from "@/Hooks/UsePlan";
import useTranslation from "@/Hooks/UseTranslation";
import { useProject } from "@/Hooks/UseProject";
import { useProgrammation } from "@/Hooks/UseProgrammation";

import {
  filterByColorCodes,
  filterByDate,
  filterByGroups,
  filterByStatus,
  filterByTypes,
  filterByUsers,
  getCollapsedGroups,
  getCollapsedTasks,
  getMacroResult,
  getRestrictionsTableItems
} from "@/Helpers/ProgrammationHelper";

import {
  setResourcesRestrictionsItemsAction
} from "@/Redux/Actions/ResourcesRestrictionsActions";
import InterfaceActions from "@/Redux/Actions/InterfaceActions";
import { setMacroAction } from "@/Redux/Actions/MacroActions";

import ProjectLayout from "@/Layouts/MainLayout/ProjectLayout";
import ContentHeader from "@/Layouts/MainLayout/components/ContentHeader";
import ContentBody from "@/Layouts/MainLayout/components/ContentBody";

import TaskFilters from '@/Components/TaskFilters/TaskFilters';
import Icon from "@/Components/Icon/Icon";
import RestrictionsGroup from "@/Components/RestrictionsGroup/RestrictionsGroup";
import ProgrammationManager from "@/Components/ProgrammationManager/ProgrammationManager";

import './ProjectResourcesRestrictionsPage.scss'

interface TableRowId {
  id: string;
  description: string;
  checked: boolean;
  itemId: string
  taskId: string
  taskName: string
  taskReleasesAt: string
  createdAt: string
  updatedAt: string
  creator: string
  responsible?: any
  responsibles?: any[]
  days: number
}

const ProjectResourcesRestrictionsPage = (props: any) => {

  const { t } = useTranslation()

  const {
    tooManyRestrictions,
    showTooManyRestrictionsModal,
  } = usePlan()

  const dispatch = useDispatch();

  const { project_id, project } = useProject()

  const { openItem, programmationLoaded, loadProgrammation } = useProgrammation()

  const projectLoadStatus = useSelector((state: any) => state.ProjectsReducer.loadRowStatus)
  const teams = useSelector((state: any) => state.TeamsReducer.result)
  const programmation = useSelector((state: any) => state.ProgrammationReducer)
  const macro = useSelector((state: any) => state.MacroReducer)
  const restrictions = useSelector((state: any) => state.ResourcesRestrictionsReducer)

  const [tab, setTab] = useState('ABIERTAS');
  const [editable, setEditable] = useState(false);
  const [showTaskFilters, setShowTaskFilters] = useState(false)
  const [showRestrictionsGroup, setShowRestrictionsGroup] = useState(false)
  const [mode, setMode] = useState<'tasks' | 'groups'>('tasks')

  const [shownTooManyRestrictionsModal, setShownTooManyRestrictionsModal] = useState(!tooManyRestrictions)

  const [restrictionsGroup, setRestrictionsGroup] = useState([])

  const [sortedColumn, setSortedColumn] = useState<keyof TableRowId>('id');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  const savedFilters = localStorage.getItem(`${project_id}-restriction-filters`)

  const [filters, setFilters] = useState(savedFilters ? JSON.parse(savedFilters) : {
    user: '',
    state: '',
    team: '',
    group: '',
    code: '',
    type: '',
    start: '',
    end: '',
  })

  const [open, close] = useMemo(() => {

    const getAsGroups = (restrictions: any[]) => {

      const groups: any[] = []

      restrictions.forEach((restriction: any) => {

        const key = `${restriction.parentId}${restriction.description}`

        const found = groups.find((group: any) => group.key === key)

        if (found) {

          found.items.push(restriction)
        } else {

          groups.push({
            ...restriction,
            key,
            taskId: restriction.parentPosition,
            taskName: restriction.parentName || t('RESOURCES_RESTRICTIONS:WithoutGroup'),
            creator: '',
            taskReleasesAt: '',
            days: '',
            createdAt: '',
            items: [restriction]
          })
        }
      })

      return groups
    }

    const rows = [...restrictions.items].sort((a, b) => {
      const valueA = a[sortedColumn];
      const valueB = b[sortedColumn];
      if (valueA < valueB) {
        return sortOrder === 'asc' ? -1 : 1;
      }
      if (valueA > valueB) {
        return sortOrder === 'asc' ? 1 : -1;
      }
      return 0;
    })
    const openRows = rows.filter((row) => row.checked === false)
    const closeRows = rows.filter((row) => row.checked === true)

    let open: any[] = openRows
    let closed: any[] = closeRows

    if (mode === 'groups') {

      open = getAsGroups(openRows)
      closed = getAsGroups(closeRows)
    }

    return [open, closed]
  }, [restrictions.items, sortedColumn, sortOrder, mode])

  const hasActiveFilters = () => {

    const values = Object.values(filters)
    const found = values.findIndex(f => f !== '')

    return !!(found >= 0)
  }

  const handleChangeFilters = (filters: any) => {

    localStorage.setItem(`${project_id}-restriction-filters`, JSON.stringify(filters))

    loadRestrictions(filters)
    setFilters(filters)
  }

  const loadRestrictions = async (withFilters?: any) => {

    if (programmation.loadStatus !== 'loaded') return

    const tasks = programmation.tasks.map((task: any) => {

      return {
        ...task,
        responsibles: task.responsibles.map((resp: any) => programmation.users.find((user: any) => user._id === resp))
      }
    })

    const options = {
      ...programmation,
      list: JSON.parse(JSON.stringify(programmation.list)),
      tasks: JSON.parse(JSON.stringify(tasks)),
      filters
    }

    let macroResult = getMacroResult(options)

    const newFilters = withFilters || filters

    if (newFilters.team) {

      const teams_ids = newFilters.team.split(',')

      const teamsFound = teams.filter((team: any) => teams_ids.includes(team._id))

      const users_ids: string[] = []

      teamsFound.forEach((team: any) => {

        team.users.forEach((user: any) => {

          if (!users_ids.includes(user._id))
            users_ids.push(user._id)
        })
      })

      macroResult = filterByUsers(macroResult, users_ids)
    } else if (newFilters.user)
      macroResult = filterByUsers(macroResult, newFilters.user.split(','))

    if (newFilters.group) {

      macroResult = filterByGroups(macroResult, newFilters.group.split(','))
    }else if (newFilters.code)
      macroResult = filterByColorCodes(macroResult, newFilters.code.split(','))

    if (newFilters.type)
      macroResult = filterByTypes(macroResult, newFilters.type.split(','))

    if (newFilters.state)
      macroResult = filterByStatus(macroResult, newFilters.state.split(','))

    if (newFilters.start || newFilters.end)
      macroResult = filterByDate(macroResult, newFilters.start, newFilters.end)

    const collapsedGroups = getCollapsedGroups(project_id, 'macro')
    const collapsedTasks = getCollapsedTasks(project_id, 'macro')

    dispatch(setMacroAction(macroResult, project.startsAt, project.endsAt, collapsedGroups, collapsedTasks))
  }

  const changeTab = (tab: string) => {
    if (!editable) {
      setTab(tab);
    }
  };

  const handleEditItem = async (item_data: any) => {

    openItem({
      item_id: item_data.itemId,
      noRedirect: true
    })
  }

  const handleSortByColumn = (column: keyof TableRowId) => {
    if (sortedColumn === column) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortedColumn(column);
      setSortOrder('asc');
    }
  };

  useEffect(() => {

    if (!programmationLoaded) return

    if (macro.loadStatus === 'none') {

      loadRestrictions()
    }
  }, [programmationLoaded, macro.loadStatus])

  useEffect(() => {

    if (macro.loadStatus !== 'loaded') return

    const options = {
      itemsList: macro.result
    }

    const tableItems = getRestrictionsTableItems(options)

    dispatch(setResourcesRestrictionsItemsAction(tableItems))

  }, [restrictions.loadItemsStatus, macro.loadStatus, macro.result]);

  useEffect(() => {

    const projectLoaded = projectLoadStatus === 'loaded'
    const taskListLoaded = macro.loadStatus === 'loaded'

    if (!projectLoaded || !taskListLoaded) {

      dispatch(InterfaceActions.showLoader())
    } else {

      dispatch(InterfaceActions.hideLoader())
    }
  }, [projectLoadStatus, macro.loadStatus])

  useEffect(() => {

    if (tooManyRestrictions) {

      setShownTooManyRestrictionsModal(false)

      showTooManyRestrictionsModal(t('RESOURCES_RESTRICTIONS:TooManyRestrictionsAlert'), project, () => setShownTooManyRestrictionsModal(true))
    }
  }, [tooManyRestrictions])

  return (
    <>
      <ProjectLayout menuName="resources" subMenuName="restrictions">
        <ContentHeader title={t('RESOURCES_RESTRICTIONS:Title')}>
          <div className="ul-widget__head-nav">
            <Tab.Container>
              <Nav as="ul" className="nav-tabs nav-tabs-line nav-tabs-bold">
                <button className={`btn btn-link btn-link-header ${restrictions.loadStatus === 'loading' ? 'active' : ''} d-inline-block`}
                  onClick={() => loadProgrammation()}
                  disabled={restrictions.loadStatus === 'loading'}
                >
                  <Icon name="sync" fas fw spin={restrictions.loadStatus === 'loading'} /> {' '}
                  <span className="d-none d-sm-inline"> {t('RESOURCES_RESTRICTIONS:ToolbarRefreshButton')}</span>
                </button>
                <button className={`btn btn-link btn-link-header ${mode === 'tasks' ? 'active' : ''}`}
                  onClick={() => setMode('tasks')}
                >
                  <Icon name="tasks" fas fw /> {' '}
                  <span className="d-none d-sm-inline"> {t('RESOURCES_RESTRICTIONS:ToolbarTasksButton')}</span>
                </button>
                <button className={`btn btn-link btn-link-header ${mode === 'groups' ? 'active' : ''}`}
                  onClick={() => setMode('groups')}
                >
                  <Icon name="layer-group" fas fw /> {' '}
                  <span className="d-none d-sm-inline"> {t('RESOURCES_RESTRICTIONS:ToolbarGroupsButton')}</span>
                </button>
                <button className={`btn btn-link btn-link-header ${hasActiveFilters() ? 'active' : ''}`}
                  onClick={() => setShowTaskFilters(true)}>
                  <span className="icon-filtro"></span>
                  <span className="d-none d-sm-inline"> {t('RESOURCES_RESTRICTIONS:ToolbarFilterButton')}</span>
                </button>
                <Nav.Item as="li">
                  <Nav.Link active={tab === 'ABIERTAS'} onClick={() => changeTab('ABIERTAS')}>
                    <span className="icon-aceptar"></span> {' '}
                    <span className="hide-on-mobile"> {t('RESOURCES_RESTRICTIONS:ToolbarOpenButton')} </span>
                  </Nav.Link>
                </Nav.Item>
                <Nav.Item as="li">
                  <Nav.Link active={tab === 'CERRADAS'} onClick={() => changeTab('CERRADAS')}>
                    <span className="icon-cancelar"></span> {' '}
                    <span className="hide-on-mobile"> {t('RESOURCES_RESTRICTIONS:ToolbarClosedButton')} </span>
                  </Nav.Link>
                </Nav.Item>
              </Nav>
            </Tab.Container>
          </div>
        </ContentHeader>
        <ContentBody>
          <Row>
            <Col className="mb-4">
              <Tab.Content className="no-padding mb-2">
                <Tab.Pane active={tab === 'ABIERTAS'}>
                  <Card className="table-card">
                    <div>
                      <div className="table-responsive">
                        <table className="table card-table">
                          <thead>
                            <tr>
                              <th className="text-rigth" style={{ width: 100 }}>
                                {t('RESOURCES_RESTRICTIONS:ListPositionColumn')}
                              </th>
                              <th className="text-rigth" style={{ width: 250 }}>
                                {mode === 'tasks' && t('RESOURCES_RESTRICTIONS:ListModeTaskColumn')}
                                {mode === 'groups' && t('RESOURCES_RESTRICTIONS:ListModeGroupColumn')}
                              </th>
                              <th
                                className="text-rigth clickable"
                                onClick={() => handleSortByColumn('createdAt')}
                                style={{ width: 150 }}
                              >
                                {t('RESOURCES_RESTRICTIONS:ListCreatedAtColumn')} ⇅
                                {sortedColumn === 'createdAt' && <span>{sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>}
                              </th>
                              <th
                                className="text-rigth clickable"
                                onClick={() => handleSortByColumn('description')}
                              >
                                {t('RESOURCES_RESTRICTIONS:ListRestrictionColumn')} ⇅
                                {sortedColumn === 'description' && <span>{sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>}
                              </th>
                              <th
                                className="clickable"
                                onClick={() => handleSortByColumn('creator')}
                              >
                                {t('RESOURCES_RESTRICTIONS:ListCreatorColumn')} ⇅
                                {sortedColumn === 'creator' && <span>{sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>}
                              </th>
                              <th style={{ width: 150 }}>
                                {t('RESOURCES_RESTRICTIONS:ListReleasesAtColumn')}
                              </th>
                              <th style={{ width: 150 }}>
                                {t('RESOURCES_RESTRICTIONS:ListDaysOpenColumn')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {!open.length && restrictions.loadStatus === 'loading' && (
                              <tr>
                                <td colSpan={7}>
                                  {t('RESOURCES_RESTRICTIONS:ListLoading')}
                                </td>
                              </tr>
                            )}
                            {!open.length && restrictions.loadStatus === 'loaded' && (
                              <tr>
                                <td colSpan={7}>
                                  {t('RESOURCES_RESTRICTIONS:ListNoOpenRestrictions')}
                                </td>
                              </tr>
                            )}
                            {open.map((row, index) => (
                              <tr key={index}>
                                <td>
                                  {row.taskId}
                                </td>
                                <td>
                                  <div onClick={() => handleEditItem(row)}
                                    style={{
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    <strong>{row.taskName}</strong><br />
                                  </div>
                                </td>
                                <td>{row.createdAt && moment(row.createdAt).format('DD/MM/YYYY')}</td>
                                <td>{row.description}</td>
                                <td>
                                  {row.creator}
                                </td>
                                <td>
                                  {row.taskReleasesAt && moment.utc(row.taskReleasesAt).format('DD/MM/YYYY')}
                                </td>
                                <td className="text-right">
                                  {row.days}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Card>
                </Tab.Pane>
                <Tab.Pane active={tab === 'CERRADAS'}>
                  <Card className="table-card">
                    <div>
                      <div className="table-responsive">
                        <table className="table card-table">
                          <thead>
                            <tr>
                              <th className="text-rigth" style={{ width: 100 }}>
                                {t('RESOURCES_RESTRICTIONS:ListPositionColumn')}
                              </th>
                              <th className="text-rigth" style={{ width: 250 }}>
                                {t('RESOURCES_RESTRICTIONS:ListNameColumn')}
                              </th>
                              <th
                                className="text-rigth clickable"
                                onClick={() => handleSortByColumn('createdAt')}
                                style={{ width: 150, verticalAlign: 'middle' }}
                              >
                                {t('RESOURCES_RESTRICTIONS:ListCreatedAtColumn')} ⇅
                                {sortedColumn === 'createdAt' && <span>{sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>}
                              </th>
                              <th
                                className="text-rigth clickable"
                                onClick={() => handleSortByColumn('description')}
                                style={{ verticalAlign: 'middle' }}
                              >
                                {t('RESOURCES_RESTRICTIONS:ListRestrictionColumn')} ⇅
                                {sortedColumn === 'description' && <span>{sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>}
                              </th>
                              <th
                                className="clickable"
                                onClick={() => handleSortByColumn('creator')}
                                style={{ verticalAlign: 'middle' }}
                              >
                                {t('RESOURCES_RESTRICTIONS:ListCreatorColumn')} ⇅
                                {sortedColumn === 'creator' && <span>{sortOrder === 'asc' ? ' ▲' : ' ▼'}</span>}
                              </th>
                              <th style={{ width: 150, verticalAlign: 'middle' }}>
                                {t('RESOURCES_RESTRICTIONS:ListActualReleaseDateColumn')}
                              </th>
                              <th style={{ width: 150, verticalAlign: 'middle' }}>
                                {t('RESOURCES_RESTRICTIONS:ListDaysOpenColumn')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {!close.length && restrictions.loadStatus === 'loading' && (
                              <tr>
                                <td colSpan={7}>
                                  {t('RESOURCES_RESTRICTIONS:ListLoading')}
                                </td>
                              </tr>
                            )}
                            {!close.length && restrictions.loadStatus === 'loaded' && (
                              <tr>
                                <td colSpan={7}>
                                  {t('RESOURCES_RESTRICTIONS:ListNoClosedRestrictions')}
                                </td>
                              </tr>
                            )}
                            {close.map((row, index) => (
                              <tr key={index}>
                                <td>{row.taskId}</td>
                                <td>
                                  <div onClick={() => handleEditItem(row)}
                                    style={{
                                      cursor: 'pointer',
                                      textDecoration: 'underline',
                                    }}
                                  >
                                    <strong>{row.taskName}</strong><br />
                                  </div>
                                </td>
                                <td>
                                  {moment(row.createdAt).format('DD/MM/YYYY')}
                                </td>
                                <td>{row.description}</td>
                                <td>
                                  {row.creator}
                                </td>
                                <td>
                                  {moment.utc(row.updatedAt).format('DD/MM/YYYY')}
                                </td>
                                <td>
                                  {row.days}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </Card>
                </Tab.Pane>
              </Tab.Content>
            </Col>
          </Row>
        </ContentBody>
      </ProjectLayout>

      <ProgrammationManager taskList={macro.result} />

      <TaskFilters
        show={showTaskFilters}
        type="restrictions"
        filters={filters}
        onChange={handleChangeFilters}
        onHide={() => setShowTaskFilters(false)}
      />

      {/* <TaskEditor
        show={showTaskEditor && (!tooManyRestrictions || shownTooManyRestrictionsModal)}
        task={task}
        group={group}
        taskList={restrictions.result}
        nonWorkingDays={nonWorkingDays}
        project={project}
        projectMembers={TeamsHelper.getAllMembersFromProject(teams)}
        projectStartDate={project.startsAt}
        projectEndDate={project.endsAt}
        onHide={() => handleCloseTaskEditor()}
        onUpdated={onTaskUpdated}
        onConfirmOrReject={onConfirmOrRejectTask}
        onDelete={(data: any) => onDeleteTask(data)}
        canConfirmOrReject={ability.can('confirm', 'task')}
        canDelete={ability.can('delete', 'task')}
        canUpdate={() => {

          const isPl4nner = ability.can('manage', 'all')
          const isAssistant = project.assistants?.includes(userData?.id)
          const isCollaborator = task?.responsibles.map((r: any) => r._id).includes(userData?.id)

          return isPl4nner || isAssistant || isCollaborator
        }}
      /> */}

      <RestrictionsGroup
        show={showRestrictionsGroup}
        group={restrictionsGroup}
        openOrClosed={tab}
        onHide={() => {

          setShowRestrictionsGroup(false)
        }}
      />
    </>
  );
};

export default ProjectResourcesRestrictionsPage;

